import { TelehealthCta } from "@/components/site/TelehealthCta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { InlineAppointmentCTA } from "@/components/site/InlineAppointmentCTA";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { SectionCta } from "@/components/site/SectionCta";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { medicalWebPageScript, PROCEDURE_ACTORS, SETTING_ONE_NIGHT } from "@/lib/medical-schema";
import { PROCEDURE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";

const BYPASS_URL = "https://virginiabariatric.com/services/bypass";

const BYPASS_MEDIA = ["bypassDiagram", "bypassGraph"] as const;
const BYPASS_DIAGRAM = PROCEDURE_MEDIA.bypassDiagram;

const BYPASS_FAQS: Faq[] = [
  {
    q: "How much weight do patients lose after gastric bypass?",
    a: (
      <>
        Most patients lose roughly 70% of their excess weight, and most of that happens in the first 12 to 18 months. Long-term results depend on follow-up, nutrition, and activity.{" "}
        <Link to="/dr-fitzer-dissects-the-news/$slug" params={{ slug: "our-ten-year-weight-loss-data" }} className="text-brand-deep underline hover:no-underline">
          Our ten year data can also be seen here.
        </Link>
      </>
    ),
    schemaAnswer:
      "Most patients lose roughly 70% of their excess weight, and most of that happens in the first 12 to 18 months. Long-term results depend on follow-up, nutrition, and activity. Our ten year data can also be seen here.",
  },
  {
    q: "Is gastric bypass better than the gastric sleeve?",
    a: (
      <>
        Neither is better across the board. Weight loss and diabetes remission run similar. Bypass is generally preferred if you have significant acid reflux or diabetes that is hard to control. The sleeve is a simpler operation with fewer long-term nutritional demands. See our detailed{" "}
        <Link to="/services/bypass-vs-sleeve" className="text-brand-deep underline hover:no-underline">
          bypass vs sleeve comparison
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "Neither is better across the board. Weight loss and diabetes remission run similar. Bypass is generally preferred if you have significant acid reflux or diabetes that is hard to control. The sleeve is a simpler operation with fewer long-term nutritional demands. See our detailed bypass vs sleeve comparison",
  },
  {
    q: "Is gastric bypass reversible?",
    a: "Yes, but reversal is very rarely called for. Gastric bypass surgery is meant to be permanent.",
  },
  {
    q: "What vitamins do I have to take after gastric bypass?",
    a: "The recommendation is to take these supplements for life: a bariatric multivitamin, calcium citrate in divided doses, and vitamin D. Without supplementation, there is a substantial risk of various nutrient deficiencies. They can cause problems like anemia, bone loss, and others.",
  },
  {
    q: "How long is recovery after gastric bypass surgery?",
    a: "The operation is laparoscopic, done through several small incisions. Most patients stay one night in the hospital and return to normal activity within about four weeks, moving through liquid, pureed, and soft diets along the way.",
  },
  {
    q: "Is gastric bypass covered by insurance?",
    a: "Most major insurers cover Roux-en-Y gastric bypass when you meet their medical criteria, usually a BMI of 40 or higher, or 35 or higher with an obesity-related condition. (Those numbers do not reflect the current NIH criteria.) Our team verifies your benefits and manages the authorization.",
  },
];

export const Route = createFileRoute("/services/bypass")({

  head: () => ({
    meta: [
      { title: "Gastric Bypass Surgery in Northern Virginia | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Laparoscopic Roux-en-Y gastric bypass with Dr. Matthew Fitzer — the long-standing gold standard for sustained weight loss and reflux relief.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Gastric Bypass Surgery in Northern Virginia | Virginia Bariatric" },
        { property: "og:description", content: "Laparoscopic Roux-en-Y gastric bypass with Dr. Matthew Fitzer — the long-standing gold standard for sustained weight loss and reflux relief." },
        { property: "og:url", content: "https://virginiabariatric.com/services/bypass" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: `https://virginiabariatric.com${BYPASS_DIAGRAM.src}` },
        { property: "og:image:alt", content: BYPASS_DIAGRAM.alt.en },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://virginiabariatric.com${BYPASS_DIAGRAM.src}` },
        { name: "twitter:image:alt", content: BYPASS_DIAGRAM.alt.en },
        { name: "twitter:title", content: "Gastric Bypass Surgery in Northern Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: "Laparoscopic Roux-en-Y gastric bypass with Dr. Matthew Fitzer — the long-standing gold standard for sustained weight loss and reflux relief." },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/services/bypass") : []),
        
      // LCP candidate: preload the WebP the <picture> element actually uses.
      // imagesrcset/imagesizes must mirror the figure's <source> so the
      // browser preloads the same candidate it will select (800w on most
      // screens) instead of force-fetching the 1536w file a second time.
      {
        rel: "preload",
        as: "image",
        href: BYPASS_DIAGRAM.webp,
        imagesrcset: BYPASS_DIAGRAM.webpSrcSet,
        imagesizes: "(min-width: 768px) 448px, calc(100vw - 3rem)",
        type: "image/webp",
      },
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/services/bypass"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": `${BYPASS_URL}#procedure`,
          mainEntityOfPage: { "@id": `${BYPASS_URL}#webpage` },
          procedureType: "https://schema.org/SurgicalProcedure",
          name: 'Roux-en-Y Gastric Bypass',
          description: "Learn how Roux-en-Y gastric bypass works, who is a candidate, and what to expect.",
          url: "https://virginiabariatric.com/services/bypass",
          bodyLocation: ["Stomach", "Small intestine"],
          howPerformed: "Performed laparoscopically under general anesthesia, the surgeon creates a small gastric pouch and connects it to a Roux limb of small intestine, bypassing the remainder of the stomach and the duodenum to restrict intake and reduce absorption. " + SETTING_ONE_NIGHT,
          preparation: "Preoperative medical, nutritional, and psychological evaluation, laboratory testing, and a pre-surgery liver-shrinking diet.",
          followup: "Lifelong vitamin supplementation with scheduled nutrition and medical follow-up visits.",
          image: procedureImageObjects(BYPASS_MEDIA, "en", BYPASS_URL),
          ...PROCEDURE_ACTORS,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema(BYPASS_FAQS, BYPASS_URL)),
      },
    ] : [],

  }),
  component: BypassPage,
});

function BypassPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif text-center">Laparoscopic Roux-en-Y Gastric Bypass in Northern Virginia</h1>
      </header>

      <MedicalReviewBanner className="mt-4" />

      <p className="mt-4 text-foreground/80">
        The Roux-en-Y gastric bypass has been the gold standard of bariatric surgery for more than
        thirty years.<sup className="text-brand">1</sup> Dr. Fitzer performs it laparoscopically, through several small incisions
        instead of one long one. That means less pain, a shorter hospital stay, and a faster
        recovery.
      </p>

      <TelehealthCta />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">What happens during bypass surgery</h2>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div>
          <p className="text-foreground/80">
            Gastric bypass surgery makes two changes. It shrinks your stomach, and it changes the
            path food takes through your GI tract. Dr. Fitzer does the surgery through six
            half-inch incisions with specialized, tiny instruments. He watches his work on a small
            camera that fits through one of the holes.
          </p>
          <h3 className="mt-4 text-[1.05rem] md:text-[1.15rem] font-serif text-foreground">
            Making the stomach pouch
          </h3>
          <p className="mt-2 text-foreground/80">
            Dr. Fitzer uses a special stapler to divide the stomach into two parts, a small pouch
            and a remnant stomach. Though small, the pouch is the important part. It’s about the
            size of a small egg. The rest of the stomach – the remnant – also stays put. It remains
            inside you, and it remains healthy.
          </p>
          <p className="mt-2 text-foreground/80">
            The operation is called gastric bypass because it causes food to “bypass” the remnant,
            which is most of the stomach. We thus literally have a “gastric bypass.”
            <sup className="text-xs">[1]</sup>
          </p>
          <p className="mt-2 text-foreground/80">
            Even though food never enters the stomach remnant, the remnant still plays a part in
            digestion. For instance, it makes important digestive enzymes that meet the food further
            downstream. Because the remnant is left inside, the bypass can be reversed. It is quite
            uncommon for that need ever to come up, however.
          </p>
          <h3 className="mt-4 text-[1.05rem] md:text-[1.15rem] font-serif text-foreground">
            The intestine is then divided
          </h3>
          <p className="mt-2 text-foreground/80">
            During surgery, Dr. Fitzer uses the same stapler device to divide the small intestine,
            which leaves two free ends. He connects one end directly to the new pouch. We call that
            end the Roux limb. He connects the other end of the small bowel to the side of the Roux
            limb a few feet downstream from the pouch. Food eaten by bypass patients travels through
            the Roux limb.
          </p>
        </div>

        <ProcedureFigure
          media="bypassDiagram"
          priority
          lightbox
          className="flex flex-col items-center md:sticky md:top-4"
          imgClassName="w-full max-w-md h-auto rounded-lg border border-border"
          captionClassName="mt-2 text-center text-sm text-foreground/70"
          sizes="(min-width: 768px) 448px, calc(100vw - 3rem)"
        />
      </div>

      <p className="mt-4 text-foreground/80">
        The operation usually takes Dr. Fitzer a little over an hour.{" "}
        <a
          href="https://www.youtube.com/watch?v=xHO4zD_5OaM&rco=1"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand underline hover:opacity-80"
        >
          You can watch Dr. Fitzer perform a gastric bypass on our YouTube channel, here
        </a>
        .
      </p>

      <p className="mt-3 text-foreground/80">
        Two mechanisms make it work. The first is restriction. A full explanation or restriction
        would be lengthy, but the practical effect is simple: It takes far less food for you to
        reach the point where you want to stop eating.
      </p>

      <p className="mt-3 text-foreground/80">
        The second is hormonal. Rerouting food changes how and when your body releases gut hormones
        such as GLP-1, PYY, and ghrelin. Those hormones shape hunger, fullness, and insulin
        sensitivity. The hormonal shift happens immediately. It is why diabetes patients often see
        their blood sugar improve right away after surgery, before they have lost much weight.
      </p>

      <p className="mt-3 text-foreground/80">
        Trying to decide between the two most common operations? Read our{" "}
        <Link to="/services/bypass-vs-sleeve" className="text-brand-deep underline hover:no-underline">
          side-by-side comparison of gastric bypass and sleeve gastrectomy
        </Link>
        .
      </p>


      <InlineAppointmentCTA
        heading="Is this the right operation for you?"
        body="Dr. Fitzer will review your health history, reflux symptoms, diabetes control, and prior surgeries to help you decide between bypass, sleeve, and other options."
        buttonText="Schedule an Appointment to discuss gastric bypass surgery"
        secondaryButton={{ text: "Check Gastric Bypass Insurance Coverage", to: "/resources/insurance-coverage" }}
      />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Are you a candidate for gastric bypass?</h2>
      <p className="mt-3 text-foreground/80">
        The current ASMBS and IFSO guidelines set the bar at a BMI of 35 or higher, whatever else is
        going on. A BMI of 30 to 34.9 also qualifies if you have metabolic disease. That means type
        2 diabetes, high blood pressure, abnormal cholesterol, sleep apnea, or fatty liver.
      </p>
      <p className="mt-3 text-foreground/80">
        The BMI thresholds are somewhat different for patients of East Asian descent.{" "}
        <Link to="/resources/qualifications" className="text-brand-deep underline hover:no-underline">
          A full explanation is in our bariatric surgery requirements.
        </Link>
      </p>
      <p className="mt-3 text-foreground/80">
        The consensus view among weight loss surgeons is that the bypass is a wiser choice than the
        sleeve in some clinical settings. They include: bad reflux, a{" "}
        <Link to="/services/hiatal-hernia-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">hiatal hernia</Link>, diabetes that
        is hard to control, or an earlier sleeve that did not produce enough weight loss. If a hiatal
        hernia is present, it is repaired during the same operation, which adds to the anti-reflux benefit.
      </p>
      <p className="mt-3 text-foreground/80">
        Most plans and Medicare cover the surgery, though the rules vary. Insurers often ask for a
        supervised diet program, usually three to six months. A brief mental health evaluation and a nutrition evaluation are also required. Our team checks your benefits and files
        the paperwork. Plan on one to three months from first visit to approval.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Preparing for and recovering from surgery</h2>
      <p className="mt-3 text-foreground/80">
        Before surgery, you will meet several times with our bariatric dietitian. All patients
        complete lab work and an EKG. Other testing, like a sleep study or an upper
        endoscopy, is not routinely ordered but is sometimes necessary for safe surgery. The
        determination is made on the basis of medical need.
      </p>
      <p className="mt-3 text-foreground/80">
        Smoking and all nicotine products have to stop completely. They raise the risk of blood
        clots and ulcers. A three-day liquid diet shrinks your liver and makes the operation safer.
      </p>
      <p className="mt-3 text-foreground/80">
        Nearly all patients are up and walking within hours of surgery and go home the next day.
        Desk work is usually reasonable at one to two weeks. Avoid lifting more than 25 pounds for
        four weeks. Walking starts right away, and it is one of the best defenses against blood
        clots.
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Weight-loss results after bypass</h2>
      <p className="mt-3 text-foreground/80">
        The large national databases, such as the MBSAQIP, tell a consistent story. On average, US
        bypass patients lose 60 to 70 percent of their excess body weight over the first 12 to 18
        months. That works out to roughly 30 percent of total body weight. Twelve-year follow-up
        published in the New England Journal of Medicine found patients held onto about 27 percent
        total weight loss a decade out.
      </p>
      <p className="mt-3 text-foreground/80">
        Dr. Fitzer’s results run somewhat above average. Weight loss usually peaks near 78% of
        excess weight around eighteen months. After a small dip it holds steady. Here is the
        ten-year weight loss data for our bypass patients as of mid-July 2026:
      </p>

      <ProcedureFigure
        media="bypassGraph"
        lightbox
        className="mt-4"
        imgClassName="w-full h-auto rounded-lg border border-border"
        sizes="(min-width: 768px) 768px, 100vw"
      />
      <p className="mt-3 text-foreground/80">
        As the chart shows, weight loss stays close to the 70% line, with a little wobble, even ten
        years out.
      </p>
      <SectionCta to="/appointments" label="cta_bypass_after_results">
        Schedule a Sit-Down Discussion with Dr. Fitzer
      </SectionCta>

      <ul className="mt-4 list-disc pl-6 space-y-2 text-foreground/80">
        <li>
          <strong>
            <Link to="/resources/obesity-and-diabetes" className="text-brand-mid underline">
              Type 2 diabetes
            </Link>
            :
          </strong>{" "}
          remission in 60 to 80 percent of patients within two
          years. Between 40 and 50 percent are still in remission at 10 to 15 years. The shorter
          your diabetes history, the better your odds.
        </li>
        <li>
          <strong>High blood pressure, high cholesterol, and sleep apnea:</strong> most patients see
          substantial improvement, and many see these conditions resolve.
        </li>
        <li>
          <strong>Long-term survival:</strong> large studies show bypass patients have significantly
          lower overall mortality than similar patients who chose non-surgical care.
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        The average weight loss is not a promise or guarantee. People who see the best results tend
        to be those who attend all follow up appointments and stick to their diet and exercise
        plans.
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Gastric bypass risks and complications</h2>
      <p className="mt-3 text-foreground/80">
        In Dr. Fitzer’s hands, laparoscopic gastric bypass carries a mortality rate near 0.05
        percent. That is well below the rate for gallbladder removal. His overall complication rate
        runs around 2 percent. A few risks deserve special mention.
      </p>
      <ul className="mt-3 list-disc pl-6 space-y-2 text-foreground/80">
        <li>
          <strong>Anastomotic leak (under 0.1 percent).</strong> A leak at a staple line is the most
          serious early complication. It is why we watch for fever, a fast heart rate, or worsening
          pain. Bleeding and blood clots are possible but uncommon.
        </li>
        <li>
          <strong>Stricture (under 0.5 percent).</strong> The connection between pouch and intestine
          narrows early on. Outpatient endoscopic dilation almost always fixes it.
        </li>
        <li>
          <strong>Marginal ulcer (3 to 5 percent).</strong> Usually traceable to smoking or NSAIDs.
          Ibuprofen, naproxen, and similar drugs have to be avoided permanently.
        </li>
        <li>
          <strong>Internal hernia and bowel obstruction (under 1 percent).</strong> These can appear
          years later. Any new, persistent abdominal pain after a bypass needs prompt evaluation. Do
          not wait it out.
        </li>
        <li>
          <strong>Dumping syndrome and low blood sugar.</strong> Cramping, nausea, flushing, or
          diarrhea after large amounts of concentrated sugar or fat, and sometimes low blood sugar
          after meals. Both are managed with diet.
        </li>
      </ul>

      <h3 className="mt-6 text-[1.05rem] md:text-[1.15rem] font-serif text-foreground">
        Gallstones and nutritional deficiencies
      </h3>
      <p className="mt-2 text-foreground/80">
        Gallstones become more likely with any weight loss, including weight lost by dieting alone.
        Nutritional deficiencies are a separate matter. The average bypass patient eats about one
        fifth of the daily food they ate before surgery. Since most nutrients arrive with food,
        shortfalls are common enough that we screen for them routinely.
      </p>

      <h3 className="mt-6 text-[1.05rem] md:text-[1.15rem] font-serif text-foreground">
        Weight regain
      </h3>
      <p className="mt-2 text-foreground/80">
        Significant regain most often comes from heavy use of candy or high-calorie drinks. No
        bariatric operation yet devised can offset those two.
      </p>
      <p className="mt-3 text-foreground/80">
        Sometimes the cause is anatomic instead. When that happens, corrective surgery often
        restores weight loss. Our guide to{" "}
        <Link to="/resources/managing-weight-regain-after-bariatric-surgery" preload="intent" className="text-brand-deep underline hover:no-underline">
          managing weight regain after bariatric surgery
        </Link>{" "}
        explains the causes and the treatment options in detail.
      </p>

      <p className="mt-3 text-foreground/80">
        Covering the full range of possible complications takes Dr. Fitzer about ninety minutes. His
        complications seminar is recorded on our YouTube channel, and he will answer any question
        you have before surgery.{" "}
        <a
          href="https://www.youtube.com/watch?v=sQNTL8s4WzE"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand underline hover:opacity-80"
        >
          The seminar on complications can be watched here.
        </a>
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Life after surgery</h2>
      <p className="mt-3 text-foreground/80">
        Your diet advances from clear liquids through full liquids, pureed foods, and soft foods to
        regular texture over about seven weeks.
      </p>
      <p className="mt-3 text-foreground/80">
        Long term, the rules are straightforward. Aim for 60 to 80 grams of protein a day and eat
        the protein first. Chew thoroughly. Drink 64 ounces of fluid daily, but not within 30
        minutes of a meal, so liquid does not flush food through your pouch.
      </p>
      <p className="mt-3 text-foreground/80">
        Vitamins are not optional. The operation reduces how well you absorb iron, calcium, and B
        vitamins. The drop is modest, but it is enough that we ask patients to plan on supplements
        for life: a bariatric multivitamin, calcium citrate in divided doses, vitamin D, B12, and
        iron.{" "}
        <a
          href="https://virginiabariatric.com/__l5e/assets-v1/0a79aabf-27ff-4fa0-843d-ba9bb497b736/Nutritional_Supplement_Guidelines.pdf"
          target="_blank"
          rel="noopener noreferrer"
          type="application/pdf"
          className="text-brand underline hover:opacity-80"
        >
          The current recommendations can be accessed here.
        </a>{" "}
        Left untreated, deficiencies can cause anemia and bone loss, and in extreme cases they can
        injure nerves permanently.
      </p>
      <p className="mt-3 text-foreground/80">
        We check labs at one, three, six, and twelve months, then once a year for life. Patients who
        stay engaged with follow-up lose more weight and keep it off longer.
      </p>
      <p className="mt-3 text-foreground/80">
        One note on alcohol. You absorb it faster after a bypass, and the risk of developing an
        alcohol use disorder rises modestly.{" "}
        <Link
          to="/dr-fitzer-dissects-the-news/$slug"
          params={{ slug: "why-are-gastric-bypass-patients-at-risk-for-alcoholism" }}
          className="text-brand-deep underline hover:no-underline"
        >
          You can read more about the connection here.
        </Link>{" "}
        Alcohol is not forbidden, but drink it mindfully.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Gastric bypass in Northern Virginia</h2>
      <p className="mt-3 text-foreground/80">Dr. Fitzer performs laparoscopic Roux-en-Y gastric bypass at INOVA Fair Oaks Hospital in Fairfax, usually with a one-night stay. Your first visit, pre-op workup and long-term follow-up happen at our office at 171 Elden Street in Herndon. Patients from Arlington, Alexandria and across the D.C. area are seen there regularly.</p>
      <p className="mt-3 text-foreground/80">Comparing options? See <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery in Northern Virginia</Link>. If you have already had a bypass, see <Link to="/services/revisions/gastric-bypass" className="text-brand-deep underline hover:no-underline">gastric bypass revision and TORe in Virginia</Link>.</p>

      <ProcedureFaq heading="Frequently asked questions" faqs={BYPASS_FAQS} />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Next step: schedule a consultation</h2>

      <p className="mt-3 text-foreground/80">
        Laparoscopic gastric bypass is considered the gold standard of weight loss operations. It is
        one of the most effective and durable treatments we have for severe obesity. Dr. Fitzer, our
        dietitians, and behavioral health specialists will help you decide whether it fits you. Call
        our office, or request a consultation online. If you are not using insurance, our{" "}
        <Link to="/self-pay-bariatric-surgery" className="text-brand-deep underline hover:no-underline">
          self-pay bariatric surgery page
        </Link>{" "}
        lists the cost of bypass surgery and explains what is included.
      </p>

      <p className="mt-3 text-foreground/80">
        Not using insurance? The{" "}
        <Link to="/self-pay-bariatric-surgery" hash="gastric-bypass-cost" className="text-brand-deep underline hover:no-underline">
          self-pay cost of the gastric bypass operation
        </Link>{" "}
        is an estimated $23,200, which covers the surgeon fee, one hospital day at INOVA Fair
        Oaks Hospital, an anesthesia estimate, and a year of routine follow-up with Dr. Fitzer. See
        the available{" "}
        <Link to="/resources/prosper" className="text-brand-deep underline hover:no-underline">
          payment plans
        </Link>.
      </p>


      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>

      <div id="bypass-footnote-1" className="mt-8 text-sm text-foreground/70">
        <sup className="text-brand">1</sup> There is also another version of bypass that is occasionally performed, called the mini gastric bypass, or single-anastomosis bypass. Because it has drawbacks the Roux-en-Y does not have, the mini bypass is rarely performed.
      </div>
      <div id="bypass-footnote-2" className="mt-4 text-sm text-foreground/70">
        <sup className="text-brand">2</sup> If it occurred to you that the pouch is also a piece of stomach, you are not alone.
        Strictly speaking, calling it a stomach bypass is a misnomer. I have never been crazy about
        that name. There are other options (e.g., “stomach reduction surgery”) that convey the point of
        the procedure without creating needless confusion. In any event, “gastric bypass” seems to
        have stuck.
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}
